import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { UsersIcon } from '@heroicons/react/24/solid'
import { FC, useEffect, useState } from 'react'

import { usePermissions } from '@/api/models/auth/hooks/usePermissions'
import { useProjectList } from '@/api/models/main'
import { useUserContacts } from '@/api/models/users'
import { useInvites } from '@/api/models/users/hooks/useInvites'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { SetUserProfileRoute } from '@/components/entities/user/hooks/useUserProfileRoute'
import {
  InviteCreationStage,
  UserProfileScreens
} from '@/components/entities/user/lib/types'
import { useUserProfileStore } from '@/components/entities/user/model/store'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import EmptyListPlug from '@/components/pages/plugs/EmptyListPlug'
import { ExpandableHeader } from '@/components/shared/expandable-header/expandable-header'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import { useUserContactsStore } from '../model/store'
import { InviteCard } from './InviteCard'
import { UserContactsListSkeleton } from './UserContactsSkeleton'

type Props = {
  setUserProfileRoute: SetUserProfileRoute
  page: number
}

const UserContactsList: FC<Props> = ({ setUserProfileRoute, page }) => {
  const { reachGoal } = useYandexMetrika()
  const { setInviteCreationStage } = useUserProfileStore()
  const { data: projectList = [] } = useProjectList()
  const {
    data: invites,
    isLoading: areInvitesLoading,
    isError: areInvitesError,
    error: invitesError
  } = useInvites()
  const { selectedProjects } = useUserContactsStore()
  const permissions = usePermissions()

  const [isInvitesBlockExpanded, setIsInvitesBlockExpanded] = useState(false)

  useEffect(() => {
    if (!areInvitesLoading) {
      setIsInvitesBlockExpanded(invites.length <= 3)
    }
  }, [areInvitesLoading, invites.length])

  const [isContactsBlockExpanded, setIsContactsBlockExpanded] = useState(true)

  const {
    data: userContacts,
    isLoading: areContactsLoading,
    isError: areContactsError,
    error: contactsError
  } = useUserContacts({
    projectId: selectedProjects || projectList.map(({ bc_guid }) => bc_guid),
    page
  })

  const shouldShowButton = permissions.contactsWrite
  const areBothRequestsError = areContactsError && areInvitesError

  const onAddContactButtonClick = () => {
    setInviteCreationStage(InviteCreationStage.Create)
    setUserProfileRoute(UserProfileScreens.InviteCreation)
    reachGoal(YandexMetrikaEvent.CONTACTS_CREATE_START)
  }

  const onContactClick = (id: string) => {
    setUserProfileRoute(UserProfileScreens.Contact, id)
    reachGoal(YandexMetrikaEvent.CONTACTS_VIEW)
  }

  const onInviteClick = (id: string) => {
    setUserProfileRoute(UserProfileScreens.Invite, id)
    reachGoal(YandexMetrikaEvent.INVITES_VIEW)
  }

  if (areInvitesLoading || areContactsLoading) {
    return <UserContactsListSkeleton />
  }

  if (areBothRequestsError) {
    if (
      isForbiddenError(contactsError ?? null) ||
      isForbiddenError(invitesError ?? null)
    ) {
      return (
        <div className="mt-[32%] sm:mt-[55%]">
          <ErrorForbidden />
        </div>
      )
    }

    return (
      <div className="mt-[32%] sm:mt-[55%]">
        <ErrorCard />
      </div>
    )
  }

  if (
    !areContactsError &&
    !areInvitesError &&
    !userContacts?.length &&
    invites.length === 0
  ) {
    return (
      <>
        <EmptyListPlug
          icon={UsersIcon}
          title={
            <span className="!text-center text-24 sm:text-20">
              У вас нет активных контактов и приглашений
            </span>
          }
          description={
            <span className="text-18 sm:text-14">
              {selectedProjects
                ? 'Попробуйте сбросить фильтры, чтобы найти нужный контакт'
                : 'Тут будут контакты, у которых есть доступ в личный кабинет'}
            </span>
          }
        />
        {shouldShowButton && (
          <FixedBottomDrawerButton
            onClick={onAddContactButtonClick}
            text="Добавить контакт"
          />
        )}
      </>
    )
  }

  return (
    <>
      {invites?.length > 0 ? (
        <ExpandableHeader
          title="Приглашения"
          isExpanded={isInvitesBlockExpanded}
          onToggle={() => setIsInvitesBlockExpanded(!isInvitesBlockExpanded)}
          ariaText="Развернуть блок приглашений"
        />
      ) : null}

      <div className="flex flex-col gap-12 sm:gap-8">
        {isInvitesBlockExpanded &&
          invites.map(invite => (
            <InviteCard
              key={invite.id}
              invite={invite}
              onClick={() => onInviteClick(invite.id)}
            />
          ))}
      </div>
      {userContacts?.length > 0 ? (
        <ExpandableHeader
          className="mt-16"
          title="Активные"
          isExpanded={isContactsBlockExpanded}
          onToggle={() => setIsContactsBlockExpanded(!isContactsBlockExpanded)}
          ariaText="Развернуть блок активных контактов"
        />
      ) : null}
      {isContactsBlockExpanded && (
        <div className="flex flex-col gap-12 sm:gap-8">
          {userContacts?.map(({ id, name, customName }) => (
            <button
              key={id}
              className="flex h-[84px] w-full items-start justify-start gap-8 rounded-12 bg-white px-16 py-20"
              onClick={() => onContactClick(id)}
            >
              <div className="flex w-full flex-col">
                <div className="flex justify-between">
                  <p className="font-bold text-20 sm:text-18">{name}</p>
                  <ChevronRightIcon className="h-24 w-24 text-grey-50" />
                </div>
                {customName && (
                  <p className="text-left text-bronze-40 text-16">
                    {customName}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {shouldShowButton && (
        <FixedBottomDrawerButton
          onClick={onAddContactButtonClick}
          text="Добавить контакт"
        />
      )}
    </>
  )
}

export default UserContactsList
